import { Box, Grid, Popover } from '@material-ui/core';
import {
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  Icons,
  Tooltip,
  Typography,
} from '@nutrien/cxp-components';
import * as markerjs2 from 'markerjs2';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';

import {
  COPY,
  DELETE,
  EDIT_DETAILS,
  FILES_IMAGE_TYPE,
  FILES_TO_DOWNLOAD,
  FILES_TO_OPEN,
} from '../../utilities/constants';
import { useWindowDimensions } from '../../utilities/useWindowDimensions';
import {
  autoVersionIfFileExists,
  downloadURI,
  getFileDataBasedOnType,
  getFileExtension,
} from '../../utilities/utilityFunctions';
import AspectRatio from '../AspectRatio';
import { useStyles } from '../Thumbnails/Thumbnail.styles';
const { MARKER_JS_2_KEY } = process.env;
import mime from 'mime-db';

import { AimsFile } from '../../models/models';
import EditFileDetails from '../EditFileDetails/EditFileDetails';

interface Props {
  fileData: AimsFile;
  fileList: AimsFile[];
  index: number;
  uniqueId: string;
  updateFileList: (updatedFileList: AimsFile[]) => void;
  isShowOnReport: boolean;
  isEditable: boolean;
  isActiveDeficiency: boolean;
}

const UnSavedFiles: React.FC<Props> = ({
  fileData,
  fileList,
  index,
  uniqueId,
  updateFileList,
  isShowOnReport,
  isEditable,
}: Props) => {
  const classes = useStyles();
  const size = useWindowDimensions();
  const imgRef = React.createRef<HTMLImageElement>();
  const [fileUrl, setFileUrl] = useState<string>('');
  const [hideImage, setHideImage] = useState(true);
  const [openEditDetails, setOpenEditDetails] = useState<boolean>(false);
  const [openFileDelete, setOpenFileDelete] = useState<boolean>(false);
  const [openCopy, setOpenCopy] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const isImage = FILES_IMAGE_TYPE.some(item => fileData.type?.toLowerCase().includes(item));
  const id = uniqueId;
  const n = fileData.updatedName.lastIndexOf('.');
  const fileExtension = fileData.updatedName.substring(n + 1);
  const openPopOver = Boolean(anchorEl);
  const idForPopover = openPopOver ? id : undefined;

  const editImageFile = async (dataURL: string, fileName: string): Promise<File> => {
    const blobUrl = await (await fetch(dataURL)).blob();
    const updatedBlobFile: File = new File([blobUrl], `${fileName}`, {
      type: 'image/jpeg',
    });
    return updatedBlobFile;
  };

  const changeImageUrl = async (val: string) => {
    if (openCopy) {
      const editedBlobFile: File = await editImageFile(
        val,
        autoVersionIfFileExists(fileData.updatedName, fileList),
      );
      let name = editedBlobFile?.name;
      let ext = getFileExtension(editedBlobFile.name);
      if (ext === undefined) {
        ext = mime[editedBlobFile.type]?.extensions[0];
        name = editedBlobFile.name + '.' + ext;
      }
      const fileCopyDetails: AimsFile = Object.assign(editedBlobFile, {
        updatedName: name,
        fileDescription: fileData.fileDescription,
        showOnReport: false,
      });
      const updatedFileList: AimsFile[] = [...fileList.slice(), fileCopyDetails];
      updateFileList(updatedFileList);
      setOpenCopy(false);
    } else {
      const editedBlobFile: File = await editImageFile(val, fileData.updatedName);
      const updatedFileList: AimsFile[] = fileList.slice();
      const fileEditDetails: AimsFile = Object.assign(editedBlobFile, {
        updatedName: fileData.updatedName,
        fileDescription: fileData.fileDescription,
        showOnReport: fileData.showOnReport,
      });
      updatedFileList[index] = fileEditDetails;
      updateFileList(updatedFileList);
    }
  };

  const showMarkerArea = () => {
    if (imgRef.current !== null) {
      const markerArea = new markerjs2.MarkerArea(imgRef.current);
      markerArea.settings.displayMode = 'popup';
      markerArea.uiStyleSettings.toolbarHeight = 60;
      markerArea.uiStyleSettings.canvasBackgroundColor = '#111111';
      markerArea.uiStyleSettings.toolboxBackgroundColor = 'black';
      if (MARKER_JS_2_KEY && !markerjs2.Activator.isLicensed) {
        markerjs2.Activator.addKey(MARKER_JS_2_KEY);
      }
      const overlayImage: HTMLElement | null = document.getElementById('markerOverlay');
      if (overlayImage) {
        markerArea.targetRoot = overlayImage;
      }
      markerArea.settings.defaultColorSet = ['#e8e8e8', '#cd4f5f', '#ffd966', '#226ea6'];

      // Store original dimensions
      const originalWidth = imgRef.current.naturalWidth || size.width;
      const originalHeight = imgRef.current.naturalHeight || size.height;

      markerArea.renderHeight = originalHeight;
      markerArea.renderWidth = originalWidth;

      markerArea.addRenderEventListener(dataUrl => {
        setHideImage(true);
        if (imgRef.current) {
          imgRef.current.src = dataUrl;
          changeImageUrl(dataUrl);
        }
        changeImageUrl(dataUrl);
      });

      markerArea.addCloseEventListener(() => {
        setHideImage(true);
        setOpenCopy(false);
      });

      markerArea.show();
      setHideImage(false);
    }
  };

  const handlePopOverOpen = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget as HTMLInputElement);
  };

  const handlePopOverClose = () => {
    setAnchorEl(null);
  };

  const handleEditDetailsOpen = () => {
    setOpenEditDetails(true);
    handlePopOverClose();
  };

  const handleEditDetailsClose = () => {
    setOpenEditDetails(false);
  };

  const handleFileDelete = () => {
    const updatedFileList: AimsFile[] = fileList.slice();
    updatedFileList.splice(index, 1);
    updateFileList(updatedFileList);
    setOpenFileDelete(false);
  };

  const updateEditDetails = async (payload: {
    fileName: string;
    fileDescription: string;
    fileShowOnReport: boolean;
  }) => {
    const updatedFileList: AimsFile[] = fileList.slice();
    updatedFileList[index].updatedName = payload.fileName;
    updatedFileList[index].fileDescription = payload.fileDescription;
    updatedFileList[index].showOnReport = payload.fileShowOnReport;
    updateFileList(updatedFileList);
    setOpenEditDetails(false);
  };

  useEffect(() => {
    if (fileData) {
      setFileUrl(URL.createObjectURL(fileData));
    }
  }, [fileData]);

  useEffect(() => {
    if (isImage) {
      const element = document.getElementById(id);
      if (element) {
        element.style.background = `url("${fileUrl}") center`;
      }
    }
  }, [isImage, fileUrl]);

  useEffect(() => {
    if (openCopy) {
      document.getElementById(`view-image-${id}`)?.click();
      handlePopOverClose();
    }
  }, [openCopy]);

  return (
    <>
      <Grid>
        <img
          ref={imgRef}
          src={fileUrl}
          id={`view-image-${id}`}
          className={classes.imageBox}
          hidden={!!hideImage}
          onClick={showMarkerArea}
        />

        <div id="markerOverlay" />
      </Grid>

      <Popover
        open={openPopOver}
        anchorEl={anchorEl}
        onClose={handlePopOverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        id={idForPopover}
        classes={{ paper: classes.paper }}
      >
        <Grid container className={classes.editDetailsGrid}>
          <Grid item xs={12}>
            <Button
              variant="text"
              color="white"
              noMinHeight
              onClick={handleEditDetailsOpen}
              className={classes.editDetails}
              disabled={!isEditable}
            >
              <Typography className={classes.editDetailstext}>{EDIT_DETAILS}</Typography>
            </Button>
          </Grid>
          <Grid item xs={12}>
            <div>
              <Button
                variant="text"
                color="white"
                noMinHeight
                onClick={() => {
                  handlePopOverClose();
                  setOpenFileDelete(true);
                }}
                className={classes.editDetails}
                disabled={!isEditable}
              >
                <Typography className={classes.editDetailstext}>{DELETE}</Typography>
              </Button>
            </div>
          </Grid>
          {isImage && (
            <Grid item container xs={12}>
              <Button
                variant="text"
                color="white"
                noMinHeight
                className={classes.editDetails}
                onClick={() => setOpenCopy(true)}
                disabled={!isEditable}
              >
                <Typography className={classes.editDetailstext}>{COPY}</Typography>
              </Button>
            </Grid>
          )}
        </Grid>
      </Popover>

      {isImage ? (
        <AspectRatio ratio={0.6}>
          <Box
            className={classes.thumbnail}
            id={id}
            onClick={() => document.getElementById(`view-image-${id}`)?.click()}
          />

          <div className={classes.metadata2}>
            {isEditable && (
              <div
                aria-describedby={idForPopover}
                onClick={handlePopOverOpen}
                className={classes.verticalFeather}
              >
                <Icons.MoreVerticalFeather color="primary" />
              </div>
            )}
          </div>

          <div className={classes.metadata}>
            <Typography className={classes.fileName}>
              {fileData.updatedName && fileData.updatedName.length > 60 ? (
                <>
                  <Tooltip title={fileData.updatedName}>
                    <>
                      {fileData.updatedName.substring(0, 60)}
                      ...
                    </>
                  </Tooltip>
                </>
              ) : (
                fileData.updatedName
              )}
            </Typography>
            <Grid>
              {fileData.fileDescription && fileData.fileDescription.length > 60 ? (
                <>
                  <Typography className={classes.fileDescription}>
                    <Tooltip title={fileData.fileDescription}>
                      <>
                        {fileData.fileDescription.substr(0, 60)}
                        ...
                        <Typography className={classes.readMore} color="primary">
                          Read more
                        </Typography>
                      </>
                    </Tooltip>
                  </Typography>
                </>
              ) : (
                fileData.fileDescription
              )}
            </Grid>
            <FormControlLabel
              className={classes.showOnReport}
              control={
                <Checkbox
                  onChange={e => {
                    updateEditDetails({
                      fileName: fileData.updatedName,
                      fileDescription: fileData.fileDescription,
                      fileShowOnReport: e.target.checked,
                    });
                  }}
                  checked={fileData.showOnReport}
                  inputProps={{ 'aria-label': 'controlled' }}
                  disabled={!isEditable || (isShowOnReport && fileData.showOnReport === false)}
                />
              }
              label="Show on report"
            />
          </div>
        </AspectRatio>
      ) : (
        <Grid item container direction="column" justifyContent="center" alignItems="center" xs={12}>
          <div className={classes.photoCardAdd}>
            <AspectRatio ratio={0.6}>
              <div className={classes.metadata}>
                <Typography className={classes.fileName}>{fileData.updatedName}</Typography>
                <Grid>
                  {fileData.fileDescription && fileData.fileDescription.length > 100 ? (
                    <>
                      <Typography className={classes.fileDescription}>
                        <Tooltip title={fileData.fileDescription}>
                          <>
                            {fileData.fileDescription.substr(0, 100)}...
                            <Typography className={classes.readMore} color="primary">
                              Read more
                            </Typography>
                          </>
                        </Tooltip>
                      </Typography>
                    </>
                  ) : (
                    fileData.fileDescription
                  )}
                </Grid>
                <FormControlLabel
                  id=""
                  className={classes.showOnReport}
                  control={
                    <Checkbox
                      onChange={() => {}}
                      checked={fileData.showOnReport}
                      inputProps={{ 'aria-label': 'controlled' }}
                      disabled={!isEditable}
                    />
                  }
                  label="Show on report"
                />
              </div>
              <Box className={`${classes.centerContent} ${classes.fileBox}`} onClick={() => {}} />
              <div className={classes.fileDiv}>
                {fileExtension && (
                  <img
                    src={`${getFileDataBasedOnType(fileExtension).src}`}
                    alt={`${getFileDataBasedOnType(fileExtension).alt}`}
                    onClick={() => {
                      if (FILES_TO_OPEN.includes(fileExtension)) {
                        window.open(fileUrl, '_blank');
                      } else if (FILES_TO_DOWNLOAD.includes(fileExtension)) {
                        downloadURI(fileUrl, fileData.updatedName);
                      }
                    }}
                  />
                )}
              </div>

              <div className={classes.metadata2}>
                {isEditable && (
                  <div
                    aria-describedby={idForPopover}
                    onClick={() => {}}
                    className={classes.verticalDots}
                  >
                    <Icons.MoreVerticalFeather color="primary" />
                  </div>
                )}
              </div>
            </AspectRatio>
          </div>
        </Grid>
      )}

      {openFileDelete && (
        <Dialog
          onClose={() => setOpenFileDelete(false)}
          open={openFileDelete}
          titleText="Delete File"
          backButton={false}
          dialogActions={
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              className={classes.dialogPadding}
            >
              <Grid item>
                <Button noMinHeight color="error" variant="contained" onClick={handleFileDelete}>
                  Yes, Delete
                </Button>
              </Grid>
            </Grid>
          }
          maxWidth="xs"
          noMinHeight
        >
          <Grid container className={classes.dialogContentPadding}>
            <Grid item container xs={12} alignItems="center">
              <Typography variant="h6" className={classes.deleteText}>
                {`Are you sure want to delete "${fileData.updatedName}"?`}
              </Typography>
            </Grid>
          </Grid>
        </Dialog>
      )}

      {openEditDetails && (
        <EditFileDetails
          handleEditDetailsClose={handleEditDetailsClose}
          openEditDetails={openEditDetails}
          isShowOnReport={
            !isEditable || (isShowOnReport && fileData.showOnReport === false) ? true : false
          }
          fileData={{
            fileName: fileData.updatedName,
            fileDescription: fileData.fileDescription,
            fileShowOnReport: fileData.showOnReport,
            originalFileName: fileData.name,
            extension: fileData?.extension,
          }}
          updateEditDetails={updateEditDetails}
        />
      )}
    </>
  );
};

export default observer(UnSavedFiles);
